'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import ContactForm, { ContactFormSubmission } from './contact-form';

type Step = 'intent' | 'category' | 'deflect' | 'form' | 'done';
type Intent = 'order' | 'general';

interface Category {
  id: string;
  label: string;
  deflect?: 'track' | 'amend' | 'returns';
}

const ORDER_CATEGORIES: Category[] = [
  { id: 'track', label: 'Track my order', deflect: 'track' },
  { id: 'amend', label: 'Amend or cancel my order', deflect: 'amend' },
  { id: 'returns', label: 'Returns or refund', deflect: 'returns' },
  { id: 'missing', label: 'Missing or damaged item' },
  { id: 'order-other', label: 'Something else about my order' },
];

const GENERAL_CATEGORIES: Category[] = [
  { id: 'product', label: 'Product question' },
  { id: 'delivery', label: 'Delivery & pricing' },
  { id: 'trade', label: 'Trade account' },
  { id: 'complaint', label: 'Complaint or feedback' },
  { id: 'general-other', label: 'Something else' },
];

export default function ContactExperience() {
  const [step, setStep] = useState<Step>('intent');
  const [intent, setIntent] = useState<Intent | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const intentCardRef = useRef<HTMLButtonElement>(null);
  const [intentCardHeight, setIntentCardHeight] = useState<number | undefined>();

  useEffect(() => {
    if (step !== 'intent') return;
    const el = intentCardRef.current;
    if (!el) return;
    const update = () => setIntentCardHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [step]);

  const intentLabel = intent === 'order' ? 'Order enquiry' : intent === 'general' ? 'General enquiry' : '';
  const categories = intent === 'order' ? ORDER_CATEGORIES : GENERAL_CATEGORIES;

  function pickIntent(i: Intent) {
    setIntent(i);
    setCategory(null);
    setStep('category');
  }

  function pickCategory(cat: Category) {
    setCategory(cat);
    if (cat.deflect) setStep('deflect');
    else setStep('form');
  }

  function backToIntent() {
    setStep('intent');
    setIntent(null);
    setCategory(null);
  }

  function backToCategory() {
    setStep('category');
    setCategory(null);
  }

  function onFormDone(payload: ContactFormSubmission) {
    void payload;
    setStep('done');
  }

  function reset() {
    backToIntent();
  }

  return (
    <>
      <nav className="contact-page-crumbs" aria-label="Progress">
        {step !== 'intent' && step !== 'done' && (
          <span
            className={`contact-crumb ${step === 'category' ? 'current' : ''}`}
            role={step !== 'category' ? 'button' : undefined}
            tabIndex={step !== 'category' ? 0 : undefined}
            onClick={step !== 'category' ? backToCategory : undefined}
            onKeyDown={(e) => { if (step !== 'category' && (e.key === 'Enter' || e.key === ' ')) backToCategory(); }}
          >
            {intentLabel}
          </span>
        )}
        {(step === 'deflect' || step === 'form') && category && (
          <>
            <span className="contact-crumb-sep" aria-hidden>›</span>
            <span className="contact-crumb current">{category.label}</span>
          </>
        )}
      </nav>

      <header className="contact-page-head">
        <h1 className="contact-page-title">Contact us</h1>
        <p className="contact-page-sub">
          We&apos;re here Monday to Friday, 8am–6pm. Pick the option that matches your question — we&apos;ll get you the fastest answer.
        </p>
      </header>

      <div className={`contact-layout ${step === 'intent' ? 'contact-layout--full' : ''}`}>
        <main className="contact-main">
          {step === 'intent' && (
            <div>
              <h2 className="contact-step-title">What&apos;s your enquiry about?</h2>
              <p className="contact-step-sub">Pick the option that best matches — we&apos;ll point you in the right direction.</p>
              <div className="contact-intent-grid">
                <button ref={intentCardRef} type="button" className="contact-intent-card" onClick={() => pickIntent('order')}>
                  <span className="contact-intent-icon" aria-hidden>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 8 12 3 3 8l9 5 9-5Z" />
                      <path d="m3 8 9 5 9-5" />
                      <path d="M3 8v8l9 5 9-5V8" />
                    </svg>
                  </span>
                  <span className="contact-intent-title">An existing order</span>
                  <span className="contact-intent-desc">Tracking, amendments, returns, missing items</span>
                </button>

                <button type="button" className="contact-intent-card" onClick={() => pickIntent('general')}>
                  <span className="contact-intent-icon" aria-hidden>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
                    </svg>
                  </span>
                  <span className="contact-intent-title">General enquiry</span>
                  <span className="contact-intent-desc">Product advice, delivery, trade, complaints</span>
                </button>

                <div className="contact-intent-card contact-intent-card--email">
                  <span className="contact-intent-icon" aria-hidden>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <path d="m22 6-10 7L2 6" />
                    </svg>
                  </span>
                  <span className="contact-intent-title">Email us</span>
                  <a className="contact-intent-email" href="mailto:hello@bathroomfort.com">hello@bathroomfort.com</a>
                  <span className="contact-intent-desc">We reply within one business day</span>
                </div>
              </div>
            </div>
          )}

          {step === 'category' && (
            <div>
              <h2 className="contact-step-title">
                {intent === 'order' ? 'What do you need help with?' : 'Pick a topic'}
              </h2>
              <p className="contact-step-sub">
                {intent === 'order'
                  ? 'Choose the closest match and we’ll take you to the right place.'
                  : 'We’ll tailor the form to what you need.'}
              </p>
              <ul className="contact-cat-list">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <button type="button" className="contact-cat-btn" onClick={() => pickCategory(cat)}>
                      <span>{cat.label}</span>
                      <span className="contact-cat-arrow" aria-hidden>→</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step === 'deflect' && category && (
            <div>
              <div className="contact-deflect">
                {category.deflect === 'track' && (
                  <>
                    <div className="contact-deflect-icon" aria-hidden>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
                        <path d="M15 18H9" />
                        <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
                        <circle cx="17" cy="18" r="2" />
                        <circle cx="7" cy="18" r="2" />
                      </svg>
                    </div>
                    <h3 className="contact-deflect-title">Track your order instantly</h3>
                    <p className="contact-deflect-body">
                      No need to message us — enter your order number and email to see the latest status and delivery tracking.
                    </p>
                    <Link href="/track-order" className="contact-button">Go to order tracking</Link>
                    <button type="button" className="contact-button contact-button-ghost" onClick={() => setStep('form')}>
                      Message us anyway
                    </button>
                  </>
                )}

                {category.deflect === 'amend' && (
                  <>
                    <div className="contact-deflect-icon" aria-hidden>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 7v5l3 3" />
                      </svg>
                    </div>
                    <h3 className="contact-deflect-title">Act quickly — we dispatch fast</h3>
                    <p className="contact-deflect-body">
                      If your order hasn&apos;t shipped yet, send us a message with your order number and we&apos;ll respond within one business day.
                    </p>
                    <button type="button" className="contact-button" onClick={() => setStep('form')}>
                      Message us
                    </button>
                  </>
                )}

                {category.deflect === 'returns' && (
                  <>
                    <div className="contact-deflect-icon" aria-hidden>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 14 4 9l5-5" />
                        <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
                      </svg>
                    </div>
                    <h3 className="contact-deflect-title">365-day returns, made easy</h3>
                    <p className="contact-deflect-body">
                      Most items can be returned within 365 days for a full refund. We&apos;ll guide you through it once you message us with your order number.
                    </p>
                    <button type="button" className="contact-button" onClick={() => setStep('form')}>
                      Start a return enquiry
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {step === 'form' && category && intent && (
            <ContactForm
              subject={category.label}
              intentLabel={intentLabel}
              showOrderNumber={intent === 'order'}
              onDone={onFormDone}
            />
          )}

          {step === 'done' && (
            <div className="contact-card">
              <div className="contact-success">
                <div className="contact-success-icon" aria-hidden>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </div>
                <h2 className="contact-success-title">Message sent</h2>
                <p className="contact-success-body">
                  Thanks for getting in touch — we&apos;ll reply within one business day.
                </p>
                <button type="button" className="contact-button contact-button-ghost" onClick={reset}>
                  Send another message
                </button>
              </div>
            </div>
          )}
        </main>

        {step !== 'intent' && (
        <aside className="contact-side">
          <div
            className="contact-side-card"
            style={intentCardHeight ? { height: intentCardHeight } : undefined}
          >
            <span className="contact-side-icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <path d="m22 6-10 7L2 6" />
              </svg>
            </span>
            <div className="contact-side-label">Email us</div>
            <a className="contact-side-value" href="mailto:hello@bathroomfort.com">hello@bathroomfort.com</a>
            <div className="contact-side-meta">We reply within one business day</div>
          </div>
        </aside>
        )}
      </div>

      <style>{`
        .contact-page-crumbs {
          font-size: 0.85rem;
          color: var(--color-text-muted);
          margin-bottom: 1rem;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.35rem;
        }
        .contact-crumb {
          color: var(--color-text-muted);
        }
        .contact-crumb[role="button"] {
          cursor: pointer;
          color: var(--color-primary);
        }
        .contact-crumb[role="button"]:hover {
          text-decoration: underline;
        }
        .contact-crumb.current {
          color: var(--color-text-primary);
          font-weight: 600;
        }
        .contact-crumb-sep {
          color: var(--color-text-muted);
        }

        .contact-page-head {
          text-align: center;
          margin-bottom: 2rem;
        }
        .contact-page-title {
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.4rem;
        }
        .contact-page-sub {
          font-size: 0.95rem;
          color: var(--color-text-secondary);
          margin: 0 auto;
          max-width: 560px;
          line-height: 1.55;
        }

        .contact-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 300px;
          gap: 1rem;
          align-items: start;
        }
        .contact-layout--full {
          grid-template-columns: minmax(0, 1fr);
        }
        .contact-main {
          min-width: 0;
          scroll-margin-top: 240px;
          padding-top: 0.5rem;
        }
        .contact-side {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          position: sticky;
          top: 260px;
          align-self: start;
          height: fit-content;
          z-index: 1;
          margin-top: 4.6rem;
        }
        .contact-side-card {
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1.5rem 1.25rem;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          align-items: flex-start;
          text-align: left;
          justify-content: flex-start;
        }
        .contact-side-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 0.75rem;
          background: var(--color-off-white);
          color: var(--color-primary);
          border: 1px solid var(--color-border);
        }
        .contact-side-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 0.2rem;
        }
        .contact-side-value {
          display: block;
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--color-text-primary);
          text-decoration: none;
          word-break: break-word;
        }
        .contact-side-value:hover { color: var(--color-primary); }
        .contact-side-meta {
          font-size: 0.8rem;
          color: var(--color-text-muted);
          margin-top: 0.2rem;
        }
        @media (max-width: 860px) {
          .contact-layout { grid-template-columns: 1fr; }
          .contact-side { position: static; margin-top: 0; }
        }

        .contact-step-title {
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.35rem;
        }
        .contact-step-sub {
          font-size: 0.95rem;
          color: var(--color-text-secondary);
          margin: 0 0 1.5rem;
          line-height: 1.5;
        }

        .contact-intent-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          align-items: stretch;
        }
        @media (max-width: 960px) {
          .contact-intent-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 640px) {
          .contact-intent-grid { grid-template-columns: 1fr; }
        }
        .contact-intent-card--email {
          cursor: default;
        }
        .contact-intent-card--email:hover {
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          transform: none;
        }
        .contact-intent-email {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-text-primary);
          text-decoration: none;
          word-break: break-word;
        }
        .contact-intent-email:hover { color: var(--color-primary); }
        .contact-intent-card {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
          text-align: left;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1.5rem 1.25rem;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          cursor: pointer;
          transition: box-shadow 0.2s, transform 0.2s;
          font-family: inherit;
        }
        .contact-intent-card:hover {
          box-shadow: 0 6px 18px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }
        .contact-intent-icon {
          width: 44px;
          height: 44px;
          border-radius: 0.75rem;
          background: var(--color-off-white);
          color: var(--color-primary);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .contact-intent-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .contact-intent-desc {
          font-size: 0.85rem;
          color: var(--color-text-secondary);
          line-height: 1.45;
        }

        .contact-cat-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .contact-cat-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1rem 1.25rem;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          font-size: 0.95rem;
          font-weight: 500;
          font-family: inherit;
          color: var(--color-text-primary);
          cursor: pointer;
          text-align: left;
          transition: box-shadow 0.15s, transform 0.15s;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
        }
        .contact-cat-btn:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transform: translateX(2px);
        }
        .contact-cat-arrow {
          color: var(--color-primary);
          font-size: 1.1rem;
        }

        .contact-deflect {
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 2rem 1.5rem;
          text-align: center;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
        }
        .contact-deflect-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 72px;
          height: 72px;
          border-radius: 999px;
          background: var(--color-off-white);
          color: var(--color-primary);
          margin-bottom: 1rem;
        }
        .contact-deflect-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.5rem;
        }
        .contact-deflect-body {
          font-size: 0.95rem;
          color: var(--color-text-secondary);
          max-width: 420px;
          margin: 0 auto 1.25rem;
          line-height: 1.55;
        }

        .contact-card {
          width: 100%;
          max-width: 640px;
          margin: 0 auto;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1.75rem;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          box-sizing: border-box;
        }
        .contact-subject-chip {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 0.4rem;
          background: var(--color-off-white);
          border: 1px solid var(--color-border);
          border-radius: 0.5rem;
          padding: 0.55rem 0.85rem;
          font-size: 0.85rem;
          margin-bottom: 1.25rem;
          line-height: 1.3;
        }
        .contact-subject-label {
          color: var(--color-text-muted);
          font-weight: 500;
        }
        .contact-subject-value {
          color: var(--color-text-primary);
          font-weight: 600;
        }
        .contact-subject-sep {
          color: var(--color-text-muted);
          font-weight: 500;
        }
        .contact-subject-intent {
          color: var(--color-text-muted);
          font-size: 0.75rem;
          padding: 0.15rem 0.5rem;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 999px;
          margin-left: auto;
        }
        .contact-form-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.35rem;
        }
        .contact-form-sub {
          font-size: 0.9rem;
          color: var(--color-text-secondary);
          margin: 0 0 1.25rem;
          line-height: 1.5;
        }

        .contact-field {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          margin-bottom: 1rem;
          position: relative;
        }
        .contact-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .contact-optional {
          color: var(--color-text-muted);
          font-weight: 500;
        }
        .contact-input {
          width: 100%;
          padding: 0.7rem 0.85rem;
          font-size: 0.95rem;
          font-family: inherit;
          border: 1px solid var(--color-border);
          border-radius: 0.5rem;
          background: var(--color-white);
          color: var(--color-text-primary);
          transition: border-color 0.15s, box-shadow 0.15s;
          box-sizing: border-box;
          line-height: 1.3;
        }
        .contact-input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px rgba(27,63,35,0.12);
        }
        .contact-textarea {
          resize: vertical;
          min-height: 130px;
          line-height: 1.5;
        }
        .contact-counter {
          position: absolute;
          right: 0.1rem;
          bottom: -1.1rem;
          font-size: 0.7rem;
          color: var(--color-text-muted);
        }

        .contact-button {
          display: inline-block;
          width: 100%;
          padding: 0.85rem 1rem;
          font-size: 0.95rem;
          font-weight: 600;
          font-family: inherit;
          background: var(--color-primary);
          color: var(--color-white);
          border: none;
          border-radius: 1rem;
          cursor: pointer;
          text-align: center;
          text-decoration: none;
          margin-top: 0.75rem;
          transition: background 0.15s, opacity 0.15s;
          box-sizing: border-box;
        }
        .contact-button:hover { background: var(--color-primary-dark); }
        .contact-button:disabled { opacity: 0.6; cursor: not-allowed; }
        .contact-button-ghost {
          background: transparent;
          color: var(--color-primary);
          border: 1px solid var(--color-border);
        }
        .contact-button-ghost:hover { background: var(--color-off-white); }

        .contact-success {
          text-align: center;
          padding: 1.5rem 0.5rem 0.75rem;
        }
        .contact-success-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          border-radius: 999px;
          background: #eaf5ee;
          color: #1b5a30;
          margin-bottom: 1rem;
        }
        .contact-success-title {
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.4rem;
        }
        .contact-success-body {
          font-size: 0.95rem;
          color: var(--color-text-secondary);
          max-width: 360px;
          margin: 0 auto 1rem;
          line-height: 1.55;
        }
      `}</style>
    </>
  );
}
